/** Option names that represent a customer-facing design / format choice (Printful multi-product variants). */
const STYLE_OPTION_NAMES = new Set(['product type', 'product', 'format', 'style', 'design']);

/**
 * Parse raw API variant rows (option1/2/3) into shop-facing dimensions.
 * @param {Record<string, unknown>} v
 */
export function parseVariantDimensions(v) {
  const opts = [
    v.option1Name && v.option1Value ? { name: String(v.option1Name), value: String(v.option1Value) } : null,
    v.option2Name && v.option2Value ? { name: String(v.option2Name), value: String(v.option2Value) } : null,
    v.option3Name && v.option3Value ? { name: String(v.option3Name), value: String(v.option3Value) } : null,
  ].filter(Boolean);

  const sizeOpt = opts.find((o) => o.name.toLowerCase() === 'size');
  const colorOpt = opts.find((o) => o.name.toLowerCase() === 'color');
  const styleOpt = opts.find((o) => STYLE_OPTION_NAMES.has(o.name.toLowerCase()));

  let style = null;
  if (styleOpt?.value) {
    style = styleOpt.value.startsWith('product:') ? (v.name ? String(v.name) : null) : styleOpt.value;
  }

  return {
    style,
    color: colorOpt?.value || null,
    size: sizeOpt?.value || null,
  };
}

/**
 * Human-readable label for a mapped frontend variant (style · color · size), de-duplicated.
 * @param {{ style?: string|null, color?: string|null, size?: string|null, name?: string|null }|null|undefined} variant
 */
export function formatVariantDisplayLabel(variant) {
  if (!variant) return '';
  const parts = [];
  if (variant.style) parts.push(variant.style);
  if (variant.color && variant.color !== variant.style) parts.push(variant.color);
  if (variant.size) parts.push(variant.size);
  if (parts.length === 0 && variant.name) return String(variant.name);

  const seen = new Set();
  return parts
    .filter((p) => {
      const key = p.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(' · ');
}

/**
 * Resolve variant index from selected dimensions. Requires all present dimensions to match
 * when selected; null selection means "any" for that dimension.
 */
export function resolveVariantIndex(variants, { selectedStyle, selectedColor, selectedSize }) {
  if (!variants?.length) return null;

  const hasStyles = variants.some((v) => v.style);
  const hasColors = variants.some((v) => v.color);
  const hasSizes = variants.some((v) => v.size);

  if (!hasStyles && !hasColors && !hasSizes) return null;

  const idx = variants.findIndex((v) => {
    const styleMatch = !hasStyles || selectedStyle == null || v.style === selectedStyle;
    const colorMatch = !hasColors || selectedColor == null || v.color === selectedColor;
    const sizeMatch = !hasSizes || selectedSize == null || v.size === selectedSize;
    return styleMatch && colorMatch && sizeMatch;
  });

  return idx >= 0 ? idx : null;
}

/**
 * Best gallery / thumbnail label for a variant row — style/colour only, size intentionally
 * excluded. Mockup photos are shared across every size of a given colour (Black/S, Black/M,
 * Black/L all use the same image), so a label that includes size would give the same photo a
 * different label per size and get flagged as "ambiguous" by buildGalleryImageEntries, which
 * then falls back to a generic "Photo N" caption instead of the colour name.
 */
export function galleryLabelForVariant(variant) {
  if (!variant) return null;
  const parts = [];
  if (variant.style) parts.push(variant.style);
  if (variant.color && variant.color !== variant.style) parts.push(variant.color);
  if (parts.length === 0) return variant.name || null;

  const seen = new Set();
  return (
    parts
      .filter((p) => {
        const key = p.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .join(' · ') || null
  );
}
